/**
 * Andar Bahar — the SERVER table clock (Phase C, plan §5.3/§6.0, contract C5, code map C11).
 *
 * This is the same seam as `LocalTableClock`, wired to the real table: the round comes from
 * `GET /api/andar_bahar/state`, the joker and the deal come from the worker's decision, and
 * every rupee on screen is one the server told us about. `Page.tsx` swaps ONE import line and
 * changes nothing else — every phase it renders is still derived from `round()`.
 *
 * ── THE FOUR RULES THIS FILE OBEYS ────────────────────────────────────────────────────────
 * 1. NEVER animate a result the server did not return. There is no local RNG here, no
 *    `sampleK`, no `dealFor`. If the worker has not written the deal by `resolvesAt + 1 s`
 *    the clock STALLS in `locked` (the strip reads BETS CLOSED — DEALING, and `syncing()` is
 *    true for any UI that wants to say SYNCING) until the cards arrive. A card invented on
 *    the client is a card the wallet will not honour.
 * 2. Every wallet figure is the server's. `/bet`, `/unbet` and `/state` all return the
 *    authoritative balance; the practice `Wallet` the Page owns is DRIVEN to that number
 *    (`credit`/`debit` by the difference) and `setWalletBalance()` keeps the rest of the app
 *    in step. The only local arithmetic is the optimistic hold between a tap and its ack.
 * 3. Time is the server's. Round stamps arrive as server epoch ms and are converted ONCE into
 *    the `performance.now()` domain the shipped Page subtracts from (`toPerf`), so
 *    `serverNow()` can keep returning `performance.now()` and no Page line changes.
 * 4. A tap is optimistic but never a lie. The chip appears immediately with `pending:true`;
 *    the ack replaces it with the real bet id and the real balance; a refusal removes it and
 *    puts the money back. Nothing is ever shown as settled that the server has not settled.
 *
 * ── WHY THE DISPLAYED ROUND IS NOT ALWAYS `state.round` ───────────────────────────────────
 * The server's `round` is the oldest NON-TERMINAL row: the moment round N settles it stops
 * being `round` and becomes `lastResult`, while round N+1 (inserted `pending` at decision
 * time, opening 3 s later) becomes `round`. The player, meanwhile, is still looking at N's
 * winning card. So the clock displays "the newest round whose window has opened and whose
 * joker is known", which is N until N+1 actually opens — exactly the local clock's behaviour,
 * and the reason `myOutcome()` survives the settle instead of blinking to YOU SAT OUT.
 */

import { api, ApiError } from '@baaligames/shared-ui/lib/api';
import { setWalletBalance, refreshWallet } from '@baaligames/shared-ui/lib/wallet';
import { type Card, type Rank, type Suit, RANKS, SUITS } from '../_cards/deck';
import { type Side, payoutFor, firstSideFor } from './engine';
import { mix } from '../aviator/prng';
import { createClockSync, type ClockSync } from '../_shared/serverClock';
import { createPoller, type Poller } from '../_shared/poller';
import {
  LocalTableClock,
  CARD_MS, FAST_MS, RESULT_MS,
  type TableClock, type Round, type Bet, type Wallet, type RoundStatus,
} from './tableClock';

// Re-exported so `Page.tsx`'s single import line can name everything it used to import from
// `./tableClock` (the types, the window length) plus this implementation.
export {
  LocalTableClock, WINDOW_MS, ARM_MS, CARD_MS, FAST_MS, RESULT_MS, INTERMISSION_MS,
} from './tableClock';
export type { TableClock, Round, Bet, Wallet, RoundStatus } from './tableClock';

/* ══════════════════════════════════════════════════════════════════════════════════════════
 * Wire types — exactly what `_tables/model.js` serialises (its whitelist, not a DB row)
 * ════════════════════════════════════════════════════════════════════════════════════════ */

type SrvStatus = 'pending' | 'betting' | 'locked' | 'resolving' | 'settling' | 'settled' | 'void';
type SrvCard = { rank: string; suit: string };

type SrvResult = { k: number; side: Side | null; x: number | null; cards?: SrvCard[] } | null;

type SrvOutcome = {
  staked: number; returned: number; net: number;
  perBet?: { betId: number; market: string; amount: number; payout: number; cancelled: boolean }[];
} | null;

type SrvRound = {
  id: number; no: number; tableId: number; status: SrvStatus;
  opensAt: number | null; locksAt: number | null; resolvesAt: number | null;
  settlesAt: number | null; nextOpensAt: number | null; settledAt: number | null;
  result: SrvResult;
  /** from the adapter's `publicRound` — the joker is public, the `preroll` column is not */
  joker?: SrvCard; firstSide?: Side; payouts?: { andar: number; bahar: number };
  pot?: number; bettors?: number;
  myOutcome?: SrvOutcome;
};

type SrvMyBet = {
  betId: number; roundId: number; roundNo: number; market: string;
  amount: number; payout: number;
};

type AbState = {
  serverNow: number;
  wallet: number;
  enabled: boolean;
  bet: { min: number; max: number; presets: number[]; maxPerRound: number };
  table: { id: number; name: string; pace: string; windowMs: number; intermissionMs: number; status: string; fakeSeed: number; sortOrder: number };
  round: SrvRound | null;
  nextRound: SrvRound | null;
  lastResult: SrvRound | null;
  history: SrvRound[];
  myBets: SrvMyBet[];
};

type BetAck = {
  betId: number; roundId: number; roundNo: number; tableId: number;
  market: string; amount: number; roundTotal: number; wallet: number; duplicate?: boolean; cancelled?: boolean;
};
type UnbetAck = { betId: number; roundId: number; refunded: number; wallet: number; duplicate?: boolean };

/* ══════════════════════════════════════════════════════════════════════════════════════════
 * Constants that MUST agree with the server
 * ════════════════════════════════════════════════════════════════════════════════════════ */

/** `paces.js` PACES.andar_bahar.lock — `/bet` refuses inside this cushion before `locks_at`. */
const LOCK_MS = 500;
/** …plus the plan's 150 ms client margin (§5.3 "greys BET at locks_at − lock_ms − 150ms"). */
const LOCK_CUSHION_MS = LOCK_MS + 150;
/** `paces.js` AB_FAST_AFTER — gaps from the 10th card on run at FAST_MS. */
const FAST_AFTER = 10;
/** How long we wait past `resolvesAt` for the worker's deal before saying we are out of sync. */
const SYNCING_AFTER_MS = 1000;
/** The local re-derive tick. The Page re-renders only when something it shows changed. */
const TICK_MS = 100;

/* ══════════════════════════════════════════════════════════════════════════════════════════
 * Pure helpers
 * ════════════════════════════════════════════════════════════════════════════════════════ */

const RANK_SET = new Set<string>(RANKS as readonly string[]);
const SUIT_SET = new Set<string>(SUITS as readonly string[]);

/** A wire card → a `Card`, or null. Nothing unvalidated reaches the renderer. */
export function toCard(c: SrvCard | null | undefined): Card | null {
  if (!c || typeof c !== 'object') return null;
  const rank = String(c.rank); const suit = String(c.suit);
  if (!RANK_SET.has(rank) || !SUIT_SET.has(suit)) return null;
  return { rank: rank as Rank, suit: suit as Suit };
}

/** Milliseconds of dealing before card `j` lands (j is 1-based); j=1 lands at `dealStartsAt`. */
export function cardOffsetMs(j: number): number {
  let ms = 0;
  for (let i = 1; i < j; i++) ms += i >= FAST_AFTER ? FAST_MS : CARD_MS;
  return ms;
}

/** How many of the k cards have landed `elapsed` ms into the deal (mirrors `paces.abDealMs`). */
export function dealtCountAt(elapsedMs: number, k: number): number {
  if (!(k > 0) || elapsedMs < 0) return 0;
  let t = 0; let n = 1;
  while (n < k) {
    const gap = n >= FAST_AFTER ? FAST_MS : CARD_MS;
    if (elapsedMs < t + gap) break;
    t += gap; n += 1;
  }
  return Math.min(n, k);
}

/** `?practice=1` → the Phase-A local clock (dev sandbox), never the money path. */
function practiceRequested(): boolean {
  try { return new URLSearchParams(window.location.search).get('practice') === '1'; }
  catch { return false; }
}

const uuid = (): string => {
  try {
    const c = globalThis.crypto as Crypto | undefined;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  } catch { /* older webview */ }
  return `ab-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

/* ══════════════════════════════════════════════════════════════════════════════════════════
 * The clock
 * ════════════════════════════════════════════════════════════════════════════════════════ */

/** A chip on the table. `Bet` (side, amount, id) plus what the server needs to move it. */
type LiveBet = Bet & {
  /** false once the ack has landed */
  pending: boolean;
  /** the real `shared_round_bets.id`, null while pending */
  betId: number | null;
  /** the idempotency key this chip was bought with — one per tap, forever */
  key: string;
  roundNo: number;
  roundId: number | null;
};

export class ServerTableClock implements TableClock {
  private clock: ClockSync;
  private poller: Poller;
  private timer = 0;
  private subs = new Set<() => void>();
  private dead = false;

  /** The last `/state` we believe. Null until the first poll lands. */
  private state: AbState | null = null;
  private tableId: number | null = null;

  /** My chips, per round number — kept after the round settles so the result screen can still
   *  say what I staked (the server drops settled rounds out of `myBets`). */
  private betsByRound = new Map<number, LiveBet[]>();
  /** Chips whose ack has not landed but which the player has already taken back. */
  private cancelOnAck = new Set<string>();

  /** The server's balance, and the optimistic holds not yet reflected in it. */
  private serverWallet = 0;
  private walletKnown = false;
  private holds = new Map<string, number>();
  /** Bumped on every acked write, so a `/state` that was already in flight cannot rewind it. */
  private writeSeq = 0;

  /**
   * ONE MONEY WRITE AT A TIME (2026-09-06).
   *
   * The one-side-per-round switch fires `/unbet` and `/bet` back to back (`Page.tsx`:
   * `c.unbet(); const res = c.bet(side, total);`). Issued concurrently, the `/bet` can reach the
   * server before the refunds commit, so `model.placeBet` prices it against a book that still
   * contains the chips being cancelled: it reads the OLD stake as `existing` and the POST-debit
   * balance, and refuses with `insufficient` — or `bad_amount` against `max_bet_per_user_round`
   * at a larger stake. The refund still lands, so no rupee is lost, but the round ends with NO
   * BET after the UI has already printed "MOVED TO … ✓". The player watches a confirmed bet
   * evaporate for a reason they were never shown.
   *
   * Serialising removes the race at its source: each `/unbet` has COMMITTED (its response is only
   * sent after the transaction) before the follow-up `/bet` is issued, so the server sees exactly
   * the table the player sees. It costs one round trip per cancelled chip — tens of milliseconds
   * against a lock cushion of 500 — and it cannot reorder anything, because a link is armed only
   * once the previous write has finished. Nothing here retries: a refused write is still handled
   * by its own catch and the chain moves on.
   *
   * This is the Teen Patti clock's `writeChain`, ported back. Andar Bahar shipped first and had
   * the bug; Teen Patti was written afterwards and fixed it.
   */
  private writeChain: Promise<void> = Promise.resolve();

  private r: Round | null = null;
  private sig = '';
  private displayedNo: number | null = null;
  private syncingFlag = false;
  private lastErr: string | null = null;

  /**
   * @param tableSeed  the Phase-A local seed. Ignored on a real table: the fake crowd must be
   *                   a pure function of the SERVER's `(table_id, round_no, fake_seed)` so two
   *                   players at one table see one theatre (plan §5.4). Kept in the signature
   *                   so `Page.tsx` needs no change beyond its import.
   * @param wallet     the Page's balance sink — driven to the server's number, never trusted.
   */
  constructor(private tableSeed: number, private wallet: Wallet, _firstRoundNo = 1) {
    // Practice mode keeps the Phase-A clock alive for the dev sandbox (`?practice=1`): a
    // constructor may hand back another object, so the swap costs the Page nothing.
    if (practiceRequested()) {
      return new LocalTableClock(tableSeed, wallet, _firstRoundNo) as unknown as ServerTableClock;
    }

    this.clock = createClockSync('/andar_bahar/time');
    this.clock.startAutoSync(30_000);

    this.poller = createPoller({
      poll: () => this.pollState(),
      now: () => performance.now(),
      edges: () => {
        const r = this.r;
        if (!r) return [];
        return [r.locksAt, r.dealStartsAt, r.settlesAt, r.nextOpensAt];
      },
      busy: () => this.syncingFlag || this.holds.size > 0,
      onError: (e) => { if (e instanceof ApiError) this.lastErr = e.message; },
    });
    this.poller.start();

    this.timer = window.setInterval(() => this.tick(), TICK_MS);
  }

  /* ── the TableClock interface ────────────────────────────────────────────────────────── */

  /** The Page renders in the `performance.now()` domain; every server stamp was converted on
   *  the way in (`toPerf`), so this stays exactly what `LocalTableClock` returned. */
  serverNow(): number { return performance.now(); }

  /**
   * The round in play.
   *
   * Null until the first `/state` lands — which `Page.tsx` already handles everywhere
   * (`r?.no`, `if (!clock || !r) return`, `dealt = r?.dealt ?? []`). A placeholder round would
   * mean a fabricated joker on screen, so we return nothing rather than something untrue; the
   * cast is to the interface's non-null type, which the Page guards.
   */
  round(): Round {
    this.recompute();
    return this.r as Round;
  }

  myBets(): Bet[] {
    const no = this.displayedNo;
    if (no == null) return [];
    return this.betsByRound.get(no) ?? [];
  }

  history(): { winner: Side; k: number; first: Side }[] {
    const rows = this.state?.history ?? [];
    const out: { winner: Side; k: number; first: Side }[] = [];
    for (const h of rows) {
      const res = h.result;
      const first = h.firstSide;
      if (!res || (res.side !== 'andar' && res.side !== 'bahar') || !first) continue;
      out.push({ winner: res.side, k: Number(res.k), first });
    }
    return out;                                  // newest first, as the local clock kept it
  }

  /**
   * What this round did to me. The server's `shared_user_ledger` row is the truth and is
   * preferred the instant it exists; until settlement lands we show the outcome DERIVED from
   * the server's own decided result (`floor(stake × X)` — the same arithmetic the settle tx
   * runs), so a winner never flashes "YOU SAT OUT" in the gap between the deal and the credit.
   * Nothing here is shown before the server has revealed the result.
   */
  myOutcome(): { staked: number; returned: number; net: number } | null {
    const r = this.r;
    if (!r || (r.status !== 'result' && r.status !== 'intermission')) return null;
    const srv = this.settledOutcomeFor(this.displayedNo);
    if (srv) return srv;
    return this.provisionalOutcome();
  }

  subscribe(cb: () => void) { this.subs.add(cb); return () => { this.subs.delete(cb); }; }

  /** The server has told us the balance at least once (first `/state`, or a write ack). */
  walletReady() { return this.walletKnown; }

  /**
   * Put a chip down. Synchronous, like the local clock: the answer is the LOCAL guard's
   * (window closed / not enough money); the server's answer arrives on the ack and either
   * confirms the chip or takes it back off the table.
   */
  bet(side: Side, amount: number): { ok: true } | { ok: false; reason: 'locked' | 'balance' } {
    this.recompute();                             // decide against the phase as it is RIGHT NOW
    const r = this.r;
    const st = this.state;
    if (!r || !st || !this.bettable(r)) return { ok: false as const, reason: 'locked' as const };
    const amt = Math.floor(Number(amount));
    if (!(amt > 0)) return { ok: false as const, reason: 'balance' as const };
    if (amt > this.displayBalance()) return { ok: false as const, reason: 'balance' as const };

    const key = uuid();
    const b: LiveBet = {
      side, amount: amt, id: `p:${key}`,
      pending: true, betId: null, key,
      roundNo: r.no, roundId: this.displayedRoundId(),
    };
    this.addBet(b);
    this.holds.set(key, -amt);                    // optimistic: the chip has left the wallet
    this.pushWallet();
    this.emit();
    this.enqueue(() => this.sendBet(b));
    return { ok: true as const };
  }

  /**
   * Take every chip of this round back (the Page's UNDO, and the first half of a side switch).
   * `/unbet` is per BET (contract C0.3), so this fans out — one call per chip, each idempotent
   * on its own bet id. Returns the amount taken back, optimistically; a refusal puts the chip
   * back exactly where it was.
   */
  unbet(): number {
    this.recompute();
    const r = this.r;
    if (!r || !this.bettable(r)) return 0;
    const no = this.displayedNo;
    if (no == null) return 0;
    const list = this.betsByRound.get(no) ?? [];
    if (!list.length) return 0;

    const total = list.reduce((s, b) => s + b.amount, 0);
    this.betsByRound.set(no, []);
    for (const b of list) {
      if (b.betId != null) {
        this.holds.set(`u:${b.key}`, b.amount);   // optimistic: the chip is on its way back
        this.enqueue(() => this.sendUnbet(b));
      } else {
        // Still in flight — cancel it the moment we learn its id.
        this.cancelOnAck.add(b.key);
      }
    }
    this.pushWallet();
    this.emit();
    return total;
  }

  destroy() {
    this.dead = true;
    window.clearInterval(this.timer);
    try { this.poller?.stop(); } catch { /* already stopped */ }
    try { this.clock?.destroy(); } catch { /* already stopped */ }
    this.subs.clear();
    // The game owned the balance while it was on screen; hand the rest of the app the truth.
    void refreshWallet();
  }

  /* ── extras (not part of the interface; safe for a later UI to read) ─────────────────── */

  /** True when the deal is overdue: past `resolvesAt + 1 s` with no result from the server.
   *  A UI may show SYNCING…; this clock simply refuses to invent a card. */
  syncing(): boolean { return this.syncingFlag; }
  /** The last error message the server gave us (bet refused, limit hit). */
  lastError(): string | null { return this.lastErr; }
  /** The server's bet bounds for this game — the Page's hardcoded caps should come from here. */
  limits(): { min: number; max: number; presets: number[]; maxPerRound: number } | null {
    return this.state?.bet ?? null;
  }
  /** The live table (id, name, pace) once known. */
  table(): AbState['table'] | null { return this.state?.table ?? null; }
  /** Server epoch ms, for anything that needs the wall clock rather than the perf domain. */
  serverEpochNow(): number { return this.clock.serverNow(); }
  isSynced(): boolean { return this.clock.isSynced(); }

  /* ── polling ─────────────────────────────────────────────────────────────────────────── */

  private async pollState(): Promise<void> {
    const seqAtStart = this.writeSeq;
    const t0 = performance.now();
    const path = this.tableId == null ? '/andar_bahar/state' : `/andar_bahar/state?table=${this.tableId}`;
    const st = await api.get<AbState>(path);
    const t1 = performance.now();
    if (this.dead) return;

    // A server timestamp we can anchor on before the first `/time` burst completes.
    this.clock.noteFallback(Number(st.serverNow), t0, t1);

    this.state = st;
    this.tableId = Number(st.table?.id) || this.tableId;
    this.lastErr = null;

    // A poll is only fresher than the last write if no write acked while it was in flight:
    // the ack carries the POST-write balance and bet list, this read may predate both. When a
    // write did land, keep what the ack told us and go round again immediately.
    if (seqAtStart === this.writeSeq) {
      this.serverWallet = Number(st.wallet) || 0;
      this.walletKnown = true;
      this.mergeServerBets(st);
    } else {
      this.poller.kick();
    }
    this.pushWallet();
    this.tick();                                  // re-derive + emit on the fresh truth
  }

  /**
   * Reconcile my chips with the server's. The server is authoritative for every round it
   * still reports (`round` + `nextRound`): a chip it does not list was cancelled or never
   * landed. Chips still pending (no ack yet) are kept — the server has not seen them.
   */
  private mergeServerBets(st: AbState) {
    const live = new Set<number>();
    if (st.round) live.add(st.round.no);
    if (st.nextRound) live.add(st.nextRound.no);

    const byRound = new Map<number, LiveBet[]>();
    for (const mb of st.myBets || []) {
      const side = mb.market === 'andar' || mb.market === 'bahar' ? (mb.market as Side) : null;
      if (!side) continue;
      const arr = byRound.get(mb.roundNo) ?? [];
      arr.push({
        side, amount: Number(mb.amount), id: String(mb.betId),
        pending: false, betId: Number(mb.betId), key: `srv:${mb.betId}`,
        roundNo: Number(mb.roundNo), roundId: Number(mb.roundId),
      });
      byRound.set(mb.roundNo, arr);
    }

    for (const no of live) {
      const fromServer = byRound.get(no) ?? [];
      const mine = this.betsByRound.get(no) ?? [];
      const stillPending = mine.filter((b) => b.pending && !this.cancelOnAck.has(b.key));
      // A chip the player has just taken back must not reappear because this `/state` was
      // read before the refund committed.
      const cancelling = new Set<string>();
      for (const k of this.holds.keys()) if (k.startsWith('u:')) cancelling.add(k.slice(2));
      const kept = fromServer.filter((b) => {
        const prev = mine.find((m) => m.betId === b.betId);
        return !(prev && cancelling.has(prev.key));
      });
      this.betsByRound.set(no, [...kept, ...stillPending]);
    }

    // Forget rounds that are neither live nor on screen (a long session must not grow).
    for (const no of [...this.betsByRound.keys()]) {
      if (!live.has(no) && no !== this.displayedNo && no !== (st.lastResult?.no ?? -1)) {
        this.betsByRound.delete(no);
      }
    }
  }

  /* ── writes ──────────────────────────────────────────────────────────────────────────── */

  /** Put one money write at the back of the queue. See `writeChain`. */
  private enqueue(task: () => Promise<void>) {
    this.writeChain = this.writeChain.then(task, task).catch(() => { /* handled in the task */ });
  }

  private async sendBet(b: LiveBet) {
    try {
      const ack = await api.post<BetAck>('/andar_bahar/bet', {
        table: this.tableId ?? undefined,
        roundId: b.roundId ?? undefined,
        roundNo: b.roundNo,
        market: b.side,
        amount: b.amount,
        idempotencyKey: b.key,
        // AB never pre-bets the next round: a chip on ANDAR is priced by THIS round's joker
        // and would be silently re-priced against a different card (plan §5.3).
        allowNext: false,
      });
      if (this.dead) return;
      this.writeSeq += 1;
      this.holds.delete(b.key);
      this.serverWallet = Number(ack.wallet) || 0;
      this.walletKnown = true;

      const list = this.betsByRound.get(b.roundNo) ?? [];
      const idx = list.findIndex((x) => x.key === b.key);
      const settledBet: LiveBet = {
        ...b, pending: false, betId: Number(ack.betId), id: String(ack.betId),
        roundId: Number(ack.roundId), roundNo: Number(ack.roundNo),
      };
      if (this.cancelOnAck.has(b.key)) {
        // UNDO landed while this tap was in flight: take it straight back off the table.
        this.cancelOnAck.delete(b.key);
        if (idx >= 0) list.splice(idx, 1);
        this.holds.set(`u:${b.key}`, b.amount);
        this.enqueue(() => this.sendUnbet(settledBet));
      } else if (idx >= 0) {
        list[idx] = settledBet;
        // The server may have placed it on the round we asked for and no other; if the round
        // number came back different, move the chip to where the money actually is.
        if (settledBet.roundNo !== b.roundNo) {
          list.splice(idx, 1);
          const dst = this.betsByRound.get(settledBet.roundNo) ?? [];
          dst.push(settledBet);
          this.betsByRound.set(settledBet.roundNo, dst);
        }
      }
      this.betsByRound.set(b.roundNo, list);
      this.pushWallet();
      this.emit();
      this.poller.kick();
    } catch (e) {
      if (this.dead) return;
      // Refused: the money never left. Remove the chip and hand the hold back.
      this.holds.delete(b.key);
      this.cancelOnAck.delete(b.key);
      const list = this.betsByRound.get(b.roundNo) ?? [];
      const idx = list.findIndex((x) => x.key === b.key);
      if (idx >= 0) list.splice(idx, 1);
      this.betsByRound.set(b.roundNo, list);
      this.lastErr = e instanceof ApiError ? e.message : 'That bet did not go through.';
      this.pushWallet();
      this.emit();
      this.poller.kick();
    }
  }

  private async sendUnbet(b: LiveBet) {
    const holdKey = `u:${b.key}`;
    try {
      const ack = await api.post<UnbetAck>('/andar_bahar/unbet', {
        betId: b.betId, idempotencyKey: `${b.key}:undo`, reason: 'undo',
      });
      if (this.dead) return;
      this.writeSeq += 1;
      this.holds.delete(holdKey);
      this.serverWallet = Number(ack.wallet) || 0;
      this.walletKnown = true;
      this.pushWallet();
      this.emit();
      this.poller.kick();
    } catch (e) {
      if (this.dead) return;
      // Too late (or any refusal): the chip is STILL on the table and still at risk. Put it
      // back on screen rather than showing money the player does not have.
      this.holds.delete(holdKey);
      const list = this.betsByRound.get(b.roundNo) ?? [];
      if (!list.some((x) => x.betId === b.betId)) list.push({ ...b, pending: false });
      this.betsByRound.set(b.roundNo, list);
      this.lastErr = e instanceof ApiError ? e.message : 'That chip could not be taken back.';
      this.pushWallet();
      this.emit();
      this.poller.kick();
    }
  }

  /* ── wallet ──────────────────────────────────────────────────────────────────────────── */

  private displayBalance(): number {
    let d = 0;
    for (const v of this.holds.values()) d += v;
    return Math.max(0, (this.walletKnown ? this.serverWallet : this.wallet.balance()) + d);
  }

  /** Drive the Page's balance sink to the number we believe, using only the `Wallet` seam. */
  private pushWallet() {
    if (!this.walletKnown) return;
    const target = this.displayBalance();
    const cur = this.wallet.balance();
    const d = target - cur;
    if (d > 0) this.wallet.credit(d);
    else if (d < 0) this.wallet.debit(-d);
    setWalletBalance(this.serverWallet);          // the app-wide chip shows the SERVER balance
  }

  /* ── derivation ──────────────────────────────────────────────────────────────────────── */

  private addBet(b: LiveBet) {
    const list = this.betsByRound.get(b.roundNo) ?? [];
    list.push(b);
    this.betsByRound.set(b.roundNo, list);
  }

  private displayedRoundId(): number | null {
    const st = this.state; const no = this.displayedNo;
    if (!st || no == null) return null;
    for (const c of [st.round, st.nextRound, st.lastResult]) if (c && c.no === no) return c.id;
    return null;
  }

  /** Bets may only be placed while the window is genuinely open on the server's clock. */
  private bettable(r: Round): boolean {
    if (r.status !== 'betting') return false;
    return performance.now() < r.locksAt - LOCK_CUSHION_MS;
  }

  private settledOutcomeFor(no: number | null): { staked: number; returned: number; net: number } | null {
    const lr = this.state?.lastResult;
    if (!lr || no == null || lr.no !== no) return null;
    const o = lr.myOutcome;
    if (!o) return null;
    return { staked: Number(o.staked), returned: Number(o.returned), net: Number(o.net) };
  }

  /** `floor(stake × X)` on the server's decided result — the settle tx's own arithmetic. */
  private provisionalOutcome(): { staked: number; returned: number; net: number } | null {
    const r = this.r;
    if (!r || !r.winner) return null;
    const bets = this.myBets();
    if (!bets.length) return null;
    const x = payoutFor(r.winner, r.joker);
    let staked = 0; let returned = 0;
    for (const b of bets) {
      staked += b.amount;
      if (b.side === r.winner) returned += Math.floor(b.amount * x + 1e-9);
    }
    return { staked, returned, net: returned - staked };
  }

  /**
   * Choose the round on screen and derive its phase from the server's stamps.
   *
   * Everything here is a pure function of (`this.state`, now). No timers advance it, no local
   * randomness feeds it: if the server has not decided, the phase simply does not advance.
   */
  private recompute(): string {
    const st = this.state;
    if (!st) { this.r = null; this.displayedNo = null; return ''; }
    const now = performance.now();
    const toPerf = (ms: number | null | undefined) => (ms == null ? null : this.clock.toPerf(Number(ms)));

    // Candidates, newest first: the round in play, the one after it, and the one just settled.
    const cands = [st.nextRound, st.round, st.lastResult]
      .filter((c): c is SrvRound => !!c && !!toCard(c.joker) && c.status !== 'void')
      .sort((a, b) => b.no - a.no);
    if (!cands.length) { this.r = null; this.displayedNo = null; return ''; }

    // The newest round whose window has actually opened; before the first one opens, the
    // earliest known round (so the joker and the full countdown are on screen at boot).
    const src = cands.find((c) => {
      const o = toPerf(c.opensAt);
      return o != null && o <= now;
    }) || cands[cands.length - 1];

    const joker = toCard(src.joker) as Card;
    const firstSide: Side = src.firstSide === 'andar' || src.firstSide === 'bahar'
      ? src.firstSide : firstSideFor(joker);

    const opensAt = toPerf(src.opensAt) ?? now;
    const locksAt = toPerf(src.locksAt) ?? (opensAt + Number(st.table?.windowMs || 12_000));
    const dealStartsAt = toPerf(src.resolvesAt) ?? (locksAt + 800);
    const settlesAt = toPerf(src.settlesAt);
    const nextOpensAt = toPerf(src.nextOpensAt);

    // A deal is usable only if it is COMPLETE: k cards, every one of them a card we recognise,
    // and a side. A truncated or malformed array would make the k-th card — the match, the
    // thing the whole animation is about — the wrong card, so a partial deal counts as no deal
    // and the table waits instead.
    const res = src.result;
    const cards: Card[] = (res && Array.isArray(res.cards) ? res.cards : [])
      .map(toCard).filter((c): c is Card => c != null);
    const k = res ? Number(res.k) : NaN;
    const hasDeal = !!res && (res.side === 'andar' || res.side === 'bahar')
      && Number.isInteger(k) && k >= 1 && cards.length === k;

    // ── phase ─────────────────────────────────────────────────────────────────────────
    // The ONLY way past 'locked' is a deal the server has already written. Missing it past
    // `resolvesAt + 1 s` is a sync problem, not a licence to draw a card.
    let status: RoundStatus;
    let dealt: Card[] = [];
    this.syncingFlag = false;

    if (now < locksAt) {
      status = 'betting';
    } else if (!hasDeal || cards.length === 0) {
      status = 'locked';
      this.syncingFlag = now > dealStartsAt + SYNCING_AFTER_MS;
    } else if (now < dealStartsAt) {
      status = 'locked';
    } else {
      const shown = dealtCountAt(now - dealStartsAt, cards.length);
      const cardsDoneAt = dealStartsAt + cardOffsetMs(cards.length);
      const endsAt = settlesAt ?? (cardsDoneAt + RESULT_MS);
      if (now < cardsDoneAt) {
        status = 'dealing';
        dealt = cards.slice(0, shown);
      } else if (now < endsAt) {
        status = 'result';
        dealt = cards;
      } else {
        // Past `settles_at`: intermission until the next round opens — and PAST `nextOpensAt`
        // too, because a `pending` row carries no joker until the worker claims it. Holding on
        // the finished round beats blanking the table or inventing the next one.
        status = 'intermission';
        dealt = cards;
      }
    }

    const isDone = status === 'result' || status === 'intermission';
    const winner: Side | null = isDone && hasDeal
      ? ((res!.side as Side) ?? null)
      : null;

    this.displayedNo = src.no;
    this.r = {
      no: src.no,
      status,
      joker,
      firstSide,
      opensAt,
      locksAt,
      dealStartsAt,
      dealt,
      k: isDone && hasDeal ? k : null,
      winner,
      settlesAt,
      nextOpensAt,
      // §5.4: the theatre must be identical on every device at this table, so it is seeded
      // from the SERVER's table seed and round number — never a per-device localStorage seed.
      fakeSeed: mix(Number(st.table?.fakeSeed ?? this.tableSeed) >>> 0, src.no),
    };

    const bets = this.betsByRound.get(src.no) ?? [];
    const betSig = bets.map((b) => `${b.id}${b.side}${b.amount}${b.pending ? 'p' : ''}`).join(',');
    const out = this.myOutcomeSig();
    return `${src.no}|${status}|${dealt.length}|${betSig}|${this.displayBalance()}|${out}|${this.syncingFlag ? 's' : ''}`;
  }

  private myOutcomeSig(): string {
    const o = this.settledOutcomeFor(this.displayedNo);
    return o ? `${o.staked}/${o.returned}/${o.net}` : '';
  }

  private tick() {
    if (this.dead) return;
    const sig = this.recompute();
    if (sig === this.sig) return;
    this.sig = sig;
    this.emit();
  }

  private emit() { this.subs.forEach((cb) => { try { cb(); } catch { /* a bad subscriber must not stop the table */ } }); }
}

export default ServerTableClock;
